import React, { useState, useEffect, useCallback } from 'react';
import API from './config';
import { useIsMobile } from './useResponsive';

// Onglet « Factures reçues » (Sprint 7.6 — réforme facturation électronique).
// Deux origines coexistent durablement (calendrier d'émission échelonné jusqu'au
// 1er sept. 2027) : les factures « électroniques » récupérées via la PDP (bouton
// Synchroniser + tâche NAS quotidienne), et les factures « manuelles » saisies à la
// main pour les fournisseurs pas encore dématérialisés (en-tête + PDF justificatif).

const COULEURS = {
  primaire: '#1E3A5F',
  vert: '#16A34A',
  rouge: '#DC2626',
  orange: '#F59E0B',
  bleu: '#0EA5E9',
  texte: '#1F2937',
  gris: '#6B7280',
  fond: '#F7F8FA',
};

const SOURCE_BADGE = {
  pdp: { label: '🔁 Électronique', color: COULEURS.bleu },
  manuel: { label: '📄 Manuelle', color: COULEURS.gris },
};

const STATUT_BADGE = {
  a_traiter: { label: 'À traiter', color: COULEURS.orange },
  traitee: { label: 'Traitée', color: COULEURS.vert },
  rapprochee: { label: 'Rapprochée', color: COULEURS.bleu },
};

function formaterMontant(s) {
  if (s === null || s === undefined || s === '') return '—';
  const n = parseFloat(s);
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function formaterDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const FORM_VIDE = {
  fournisseur_nom: '', fournisseur_siren: '', fournisseur_tva: '',
  numero: '', date_emission: '', echeance: '',
  total_ht: '', total_tva: '', total_ttc: '', notes: '',
};

export default function FacturesRecues({ apiFetch }) {
  const isMobile = useIsMobile();
  const [factures, setFactures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [info, setInfo] = useState(null);

  const [detail, setDetail] = useState(null);     // facture ouverte (vue détail)
  const [showForm, setShowForm] = useState(false); // modale de saisie manuelle
  const [form, setForm] = useState(FORM_VIDE);
  const [fichier, setFichier] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [recherche, setRecherche] = useState(false);
  const [entrepriseInfo, setEntrepriseInfo] = useState(null);

  const charger = useCallback(async () => {
    setLoading(true); setErreur(null);
    try {
      const res = await apiFetch(`${API}/factures-recues`);
      if (!res.ok) throw new Error('Chargement impossible');
      setFactures(await res.json());
    } catch (e) {
      setErreur(e.message);
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => { charger(); }, [charger]);

  const synchroniser = async () => {
    setSyncing(true); setErreur(null); setInfo(null);
    try {
      const res = await apiFetch(`${API}/factures-recues/sync`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) {
        // 502 attendu tant que les identifiants PDP de l'entreprise ne sont pas configurés.
        throw new Error(data.detail || 'Synchronisation impossible');
      }
      setInfo(`Synchronisation terminée : ${data.nouvelles} nouvelle(s) facture(s) reçue(s).`);
      await charger();
    } catch (e) {
      setErreur(e.message);
    } finally {
      setSyncing(false);
    }
  };

  const ouvrirDetail = async (id) => {
    setErreur(null);
    try {
      const res = await apiFetch(`${API}/factures-recues/${id}`);
      if (!res.ok) throw new Error('Détail indisponible');
      setDetail(await res.json());
    } catch (e) {
      setErreur(e.message);
    }
  };

  const enregistrerManuelle = async (e) => {
    e.preventDefault();
    if (!form.fournisseur_nom.trim()) { setErreur('Le nom du fournisseur est obligatoire'); return; }
    setSaving(true); setErreur(null); setInfo(null);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v !== '') fd.append(k, v); });
      if (fichier) fd.append('fichier', fichier);
      const res = await apiFetch(`${API}/factures-recues`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Enregistrement impossible');
      setInfo(`Facture de ${data.fournisseur_nom} enregistrée.`);
      setShowForm(false); setForm(FORM_VIDE); setFichier(null);
      await charger();
    } catch (e) {
      setErreur(e.message);
    } finally {
      setSaving(false);
    }
  };

  const rechercherEntreprise = async () => {
    const q = (form.fournisseur_siren || '').trim();
    if (q.length < 3) { setErreur('Saisissez un SIREN (9 chiffres) ou un nom d\'entreprise'); return; }
    setRecherche(true); setErreur(null); setEntrepriseInfo(null);
    try {
      const res = await apiFetch(`${API}/entreprises/recherche?q=${encodeURIComponent(q)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Recherche impossible');
      // Préremplit depuis la source officielle (zéro faute de frappe). On garde ce que
      // l'utilisateur aurait déjà tapé si l'API ne renvoie pas le champ.
      setForm(f => ({
        ...f,
        fournisseur_nom: data.denomination || f.fournisseur_nom,
        fournisseur_siren: data.siren || f.fournisseur_siren,
        fournisseur_tva: data.tva_intra || f.fournisseur_tva,
      }));
      setEntrepriseInfo(data);
    } catch (e) {
      setErreur(e.message);
    } finally {
      setRecherche(false);
    }
  };

  const changerStatut = async (id, statut) => {
    try {
      const fd = new FormData(); fd.append('statut', statut);
      const res = await apiFetch(`${API}/factures-recues/${id}/statut`, { method: 'PATCH', body: fd });
      if (!res.ok) throw new Error('Changement de statut impossible');
      await charger();
      if (detail && detail.id === id) setDetail({ ...detail, statut_traitement: statut });
    } catch (e) {
      setErreur(e.message);
    }
  };

  const supprimer = async (id) => {
    if (!window.confirm('Supprimer définitivement cette facture saisie manuellement ?')) return;
    try {
      const res = await apiFetch(`${API}/factures-recues/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Suppression impossible');
      setDetail(null);
      await charger();
    } catch (e) {
      setErreur(e.message);
    }
  };

  const telechargerFichier = (id) => {
    // Ouvre le justificatif (cookie de session transmis automatiquement).
    window.open(`${API}/factures-recues/${id}/fichier`, '_blank');
  };

  const Badge = ({ map, val }) => {
    const b = (map[val]) || { label: val, color: COULEURS.gris };
    return <span style={{ background: b.color, color: 'white', borderRadius: '6px', padding: '2px 8px', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>{b.label}</span>;
  };

  const s = {
    wrap: { padding: isMobile ? '4px' : '8px' },
    titre: { color: COULEURS.primaire, margin: '0 0 4px' },
    sous: { color: COULEURS.gris, margin: '0 0 16px', fontSize: '14px' },
    barre: { display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' },
    btn: (bg) => ({ background: bg, color: 'white', border: 'none', borderRadius: '8px', padding: '10px 16px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }),
    msgErr: { background: '#FEF2F2', color: COULEURS.rouge, border: `1px solid ${COULEURS.rouge}`, borderRadius: '8px', padding: '10px 14px', marginBottom: '12px' },
    msgOk: { background: '#F0FDF4', color: COULEURS.vert, border: `1px solid ${COULEURS.vert}`, borderRadius: '8px', padding: '10px 14px', marginBottom: '12px' },
    table: { width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' },
    th: { textAlign: 'left', padding: '12px', background: COULEURS.fond, color: COULEURS.gris, fontSize: '13px', borderBottom: '1px solid #E5E7EB' },
    td: { padding: '12px', borderBottom: '1px solid #F1F1F1', fontSize: '14px' },
    tr: { cursor: 'pointer' },
    card: { background: 'white', borderRadius: '12px', padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.1)', borderLeft: `4px solid ${COULEURS.bleu}`, marginBottom: '10px', cursor: 'pointer' },
    cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', zIndex: 1000 },
    modal: { background: 'white', borderRadius: '12px', padding: '24px', maxWidth: '560px', width: '100%', maxHeight: '90vh', overflowY: 'auto' },
    label: { display: 'block', fontSize: '13px', color: COULEURS.gris, margin: '10px 0 4px', fontWeight: 600 },
    input: { width: '100%', padding: '9px 12px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' },
    row2: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px' },
    row3: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '10px' },
  };

  return (
    <div style={s.wrap}>
      <h2 style={s.titre}>📥 Factures reçues</h2>
      <p style={s.sous}>Factures fournisseurs — électroniques (via la plateforme) et saisies manuelles.</p>

      <div style={s.barre}>
        <button style={s.btn(COULEURS.bleu)} onClick={synchroniser} disabled={syncing}>
          {syncing ? '⏳ Synchronisation…' : '🔄 Synchroniser'}
        </button>
        <button style={s.btn(COULEURS.primaire)} onClick={() => { setForm(FORM_VIDE); setFichier(null); setEntrepriseInfo(null); setShowForm(true); }}>
          ➕ Ajouter une facture
        </button>
      </div>

      {erreur && <div style={s.msgErr}>⚠️ {erreur}</div>}
      {info && <div style={s.msgOk}>✅ {info}</div>}

      {loading ? (
        <p style={{ color: COULEURS.gris }}>Chargement…</p>
      ) : factures.length === 0 ? (
        <p style={{ color: COULEURS.gris }}>Aucune facture reçue pour l'instant.</p>
      ) : isMobile ? (
        factures.map(f => (
          <div key={f.id} style={s.card} onClick={() => ouvrirDetail(f.id)}>
            <div style={s.cardTop}>
              <div>
                <strong>{f.fournisseur_nom || '—'}</strong>
                <div style={{ fontSize: '13px', color: COULEURS.gris }}>{f.numero || 'sans n°'} · {formaterDate(f.date_emission)}</div>
              </div>
              <div style={{ textAlign: 'right' }}>{formaterMontant(f.total_ttc)}</div>
            </div>
            <div style={{ display: 'flex', gap: '6px', marginTop: '10px', flexWrap: 'wrap' }}>
              <Badge map={SOURCE_BADGE} val={f.source} />
              <Badge map={STATUT_BADGE} val={f.statut_traitement} />
            </div>
          </div>
        ))
      ) : (
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Fournisseur</th>
              <th style={s.th}>N°</th>
              <th style={s.th}>Date</th>
              <th style={s.th}>Montant TTC</th>
              <th style={s.th}>Origine</th>
              <th style={s.th}>Statut</th>
            </tr>
          </thead>
          <tbody>
            {factures.map(f => (
              <tr key={f.id} style={s.tr} onClick={() => ouvrirDetail(f.id)}>
                <td style={s.td}><strong>{f.fournisseur_nom || '—'}</strong></td>
                <td style={s.td}>{f.numero || '—'}</td>
                <td style={s.td}>{formaterDate(f.date_emission)}</td>
                <td style={s.td}>{formaterMontant(f.total_ttc)}</td>
                <td style={s.td}><Badge map={SOURCE_BADGE} val={f.source} /></td>
                <td style={s.td}><Badge map={STATUT_BADGE} val={f.statut_traitement} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* --- Modale de saisie manuelle --- */}
      {showForm && (
        <div style={s.overlay} onClick={() => !saving && setShowForm(false)}>
          <form style={s.modal} onClick={e => e.stopPropagation()} onSubmit={enregistrerManuelle}>
            <h3 style={{ marginTop: 0, color: COULEURS.primaire }}>Ajouter une facture reçue</h3>
            <p style={{ fontSize: '13px', color: COULEURS.gris, marginTop: 0 }}>
              Pour un fournisseur pas encore en facturation électronique. Joignez le PDF scanné.
            </p>

            <label style={s.label}>SIREN <span style={{ fontWeight: 400 }}>— saisissez-le puis « Rechercher » pour préremplir automatiquement</span></label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input style={{ ...s.input, flex: 1 }} value={form.fournisseur_siren}
                placeholder="9 chiffres ou nom d'entreprise"
                onChange={e => setForm({ ...form, fournisseur_siren: e.target.value })}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); rechercherEntreprise(); } }} />
              <button type="button" style={{ ...s.btn(COULEURS.bleu), whiteSpace: 'nowrap' }}
                onClick={rechercherEntreprise} disabled={recherche}>
                {recherche ? '⏳' : '🔍 Rechercher'}
              </button>
            </div>
            {entrepriseInfo && (
              <div style={{ background: entrepriseInfo.actif ? '#F0FDF4' : '#FFFBEB', border: `1px solid ${entrepriseInfo.actif ? COULEURS.vert : COULEURS.orange}`, borderRadius: '8px', padding: '8px 12px', marginTop: '8px', fontSize: '13px' }}>
                {entrepriseInfo.actif ? '✅' : '⚠️'} <strong>{entrepriseInfo.denomination}</strong>
                {entrepriseInfo.adresse ? ` — ${entrepriseInfo.adresse}` : ''}
                {!entrepriseInfo.actif && <span style={{ color: COULEURS.orange }}> (entreprise cessée selon l'INSEE)</span>}
              </div>
            )}

            <div style={s.row2}>
              <div><label style={s.label}>Fournisseur *</label>
                <input style={s.input} value={form.fournisseur_nom} required
                  onChange={e => setForm({ ...form, fournisseur_nom: e.target.value })} /></div>
              <div><label style={s.label}>N° TVA intra.</label>
                <input style={s.input} value={form.fournisseur_tva}
                  onChange={e => setForm({ ...form, fournisseur_tva: e.target.value })} /></div>
            </div>

            <div style={s.row3}>
              <div><label style={s.label}>N° facture</label>
                <input style={s.input} value={form.numero}
                  onChange={e => setForm({ ...form, numero: e.target.value })} /></div>
              <div><label style={s.label}>Date</label>
                <input style={s.input} type="date" value={form.date_emission}
                  onChange={e => setForm({ ...form, date_emission: e.target.value })} /></div>
              <div><label style={s.label}>Échéance</label>
                <input style={s.input} type="date" value={form.echeance}
                  onChange={e => setForm({ ...form, echeance: e.target.value })} /></div>
            </div>

            <div style={s.row3}>
              <div><label style={s.label}>Total HT</label>
                <input style={s.input} type="number" step="0.01" value={form.total_ht}
                  onChange={e => setForm({ ...form, total_ht: e.target.value })} /></div>
              <div><label style={s.label}>TVA</label>
                <input style={s.input} type="number" step="0.01" value={form.total_tva}
                  onChange={e => setForm({ ...form, total_tva: e.target.value })} /></div>
              <div><label style={s.label}>Total TTC</label>
                <input style={s.input} type="number" step="0.01" value={form.total_ttc}
                  onChange={e => setForm({ ...form, total_ttc: e.target.value })} /></div>
            </div>

            <label style={s.label}>Justificatif (PDF, JPG ou PNG — max 5 Mo)</label>
            <input style={s.input} type="file" accept=".pdf,.jpg,.jpeg,.png"
              onChange={e => setFichier(e.target.files[0] || null)} />

            <label style={s.label}>Notes</label>
            <textarea style={{ ...s.input, minHeight: '60px' }} value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })} />

            <div style={{ display: 'flex', gap: '10px', marginTop: '18px', justifyContent: 'flex-end' }}>
              <button type="button" style={s.btn(COULEURS.gris)} onClick={() => setShowForm(false)} disabled={saving}>Annuler</button>
              <button type="submit" style={s.btn(COULEURS.vert)} disabled={saving}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
            </div>
          </form>
        </div>
      )}

      {/* --- Vue détail --- */}
      {detail && (
        <div style={s.overlay} onClick={() => setDetail(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <h3 style={{ marginTop: 0, color: COULEURS.primaire }}>{detail.fournisseur_nom || '—'}</h3>
              <div style={{ display: 'flex', gap: '6px' }}>
                <Badge map={SOURCE_BADGE} val={detail.source} />
                <Badge map={STATUT_BADGE} val={detail.statut_traitement} />
              </div>
            </div>

            <div style={s.row2}>
              <div><label style={s.label}>N° facture</label><div>{detail.numero || '—'}</div></div>
              <div><label style={s.label}>SIREN / TVA</label><div>{detail.fournisseur_siren || '—'} {detail.fournisseur_tva ? `· ${detail.fournisseur_tva}` : ''}</div></div>
              <div><label style={s.label}>Date</label><div>{formaterDate(detail.date_emission)}</div></div>
              <div><label style={s.label}>Échéance</label><div>{formaterDate(detail.echeance)}</div></div>
              <div><label style={s.label}>Total HT</label><div>{formaterMontant(detail.total_ht)}</div></div>
              <div><label style={s.label}>TVA</label><div>{formaterMontant(detail.total_tva)}</div></div>
              <div><label style={s.label}>Total TTC</label><div><strong>{formaterMontant(detail.total_ttc)}</strong></div></div>
              <div><label style={s.label}>Reste à payer</label><div>{formaterMontant(detail.reste_a_payer)}</div></div>
            </div>

            {detail.statut_pdp && (
              <div><label style={s.label}>Statut plateforme</label><div>{detail.statut_pdp}</div></div>
            )}
            {detail.notes && (
              <div><label style={s.label}>Notes</label><div>{detail.notes}</div></div>
            )}

            {detail.lignes && detail.lignes.length > 0 && (
              <>
                <label style={s.label}>Lignes</label>
                <table style={{ ...s.table, marginTop: '4px' }}>
                  <thead><tr>
                    <th style={s.th}>Désignation</th><th style={s.th}>Qté</th><th style={s.th}>PU HT</th><th style={s.th}>TVA</th><th style={s.th}>HT</th>
                  </tr></thead>
                  <tbody>
                    {detail.lignes.map((l, i) => (
                      <tr key={i}>
                        <td style={s.td}>{l.designation || '—'}</td>
                        <td style={s.td}>{l.quantite} {l.unite || ''}</td>
                        <td style={s.td}>{formaterMontant(l.prix_unitaire_ht)}</td>
                        <td style={s.td}>{l.taux_tva ? `${l.taux_tva} %` : '—'}</td>
                        <td style={s.td}>{formaterMontant(l.montant_ht)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '18px' }}>
              {detail.a_fichier && (
                <button style={s.btn(COULEURS.bleu)} onClick={() => telechargerFichier(detail.id)}>📎 Justificatif</button>
              )}
              {detail.statut_traitement !== 'traitee' && (
                <button style={s.btn(COULEURS.vert)} onClick={() => changerStatut(detail.id, 'traitee')}>✓ Marquer traitée</button>
              )}
              {detail.statut_traitement !== 'a_traiter' && (
                <button style={s.btn(COULEURS.orange)} onClick={() => changerStatut(detail.id, 'a_traiter')}>↺ À traiter</button>
              )}
              {detail.source === 'manuel' && (
                <button style={s.btn(COULEURS.rouge)} onClick={() => supprimer(detail.id)}>🗑 Supprimer</button>
              )}
              <button style={s.btn(COULEURS.gris)} onClick={() => setDetail(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
